import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import './assets/main.css';

/* ---------------- UI Primitives ---------------- */

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-gray-200 ${className}`}>
    {children}
  </div>
);

const Button = ({ children, onClick, variant = "primary", className = "", disabled = false }) => {
  const styles = {
    primary: "bg-gray-900 text-white hover:bg-gray-800",
    secondary: "bg-white border border-gray-300 text-gray-800 hover:bg-gray-50",
    danger: "bg-red-600 text-white hover:bg-red-700"
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${styles[variant]} ${className} disabled:opacity-50`}
    >
      {children}
    </button>
  );
};

/* ---------------- Edit Set Modal ---------------- */

const EditSetModal = ({ set, onClose, onSave, languages, games, viewOnly = false }) => {
  const [name, setName] = useState(set.name);
  const [code, setCode] = useState(set.code);
  const [language, setLanguage] = useState(set.language);
  const [game, setGame] = useState(set.game || 'Pokemon');
  const [releaseDate, setReleaseDate] = useState(set.release ? set.release.split('T')[0] : '');
  const [rarities, setRarities] = useState([...set.rarities]);
  const [newRarityName, setNewRarityName] = useState('');
  const [error, setError] = useState('');

  const addRarity = () => {
    if (viewOnly) return;
    if (!newRarityName.trim()) return;
    
    if (rarities.some(r => r.name.toLowerCase() === newRarityName.toLowerCase())) {
      setError("This rarity already exists");
      return;
    }

    setRarities(prev => [...prev, { name: newRarityName.trim(), hitRate: 0 }]);
    setNewRarityName("");
    setError("");
  };

  const removeRarity = (index) => {
    setRarities(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleSave = async () => {
    setError("");

    if (!name.trim()) {
      setError("Set name is required");
      return;
    }

    if (!code.trim()) {
      setError("Set code is required");
      return;
    }

    if (rarities.length === 0) {
      setError("Please add at least one rarity");
      return;
    }

    await onSave(set._id, {
      name: name.trim(),
      code: code.trim(),
      language,
      game,
      release: releaseDate || undefined,
      rarities
    });
    
    if (!error) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center">
      <div className="bg-white rounded-xl p-6 max-w-3xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
       <div className="flex justify-between items-center mb-6 sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-semibold text-gray-900">
            {viewOnly ? 'View Set' : 'Edit Set'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
            ✕
          </button>
        </div>

        <div className="space-y-4">
          {error && (
            <div className="p-4 bg-red-100 text-red-700 rounded border border-red-300">
              {error}
            </div>
          )}

          {/* Set Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Set Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={viewOnly}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="e.g., Scarlet & Violet"
            />
          </div>

          {/* Set Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Set Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={viewOnly}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="e.g., SVI"
            />
            <p className="text-sm text-gray-500 mt-1">
              Set code will be converted to uppercase
            </p>
          </div>

          {/* Language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Language <span className="text-red-500">*</span>
            </label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              disabled={viewOnly}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              {languages.map(lang => (
                <option key={lang} value={lang}>{lang}</option>
              ))}
            </select>
          </div>

          {/* Game */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Game <span className="text-red-500">*</span>
            </label>
            <select
              value={game}
              onChange={(e) => setGame(e.target.value)}
              disabled={viewOnly}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              {games.map(gm => (
                <option key={gm} value={gm}>{gm}</option>
              ))}
            </select>
          </div>

          {/* Release Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Release Date (optional)
            </label>
            <input
              type="date"
              value={releaseDate}
              onChange={(e) => setReleaseDate(e.target.value)}
              disabled={viewOnly}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* Rarities */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rarities <span className="text-red-500">*</span>
            </label>
            
            {!viewOnly && (
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newRarityName}
                  onChange={(e) => setNewRarityName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addRarity();
                    }
                  }}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g., Common, Uncommon, Rare"
                />
                <Button onClick={addRarity} variant="secondary">
                  Add Rarity
                </Button>
              </div>
            )}

            {/* Rarities List */}
            {rarities.length > 0 && (
              <div className="space-y-2">
                {rarities.map((rarity, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-4 p-3 bg-gray-50 border rounded"
                  >
                    <input
                      type="text"
                      value={rarity.name}
                      onChange={(e) => {
                        const newRarities = [...rarities];
                        newRarities[idx].name = e.target.value;
                        setRarities(newRarities);
                      }}
                      disabled={viewOnly}
                      className="font-medium flex-1 border border-gray-300 rounded px-2 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Rarity name"
                    />
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">Hit Rate [1/?]:</label>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={rarity.hitRate}
                        onChange={(e) => {
                          const newRarities = [...rarities];
                          newRarities[idx].hitRate = Number(e.target.value);
                          setRarities(newRarities);
                        }}
                        disabled={viewOnly}
                        className="w-24 border rounded px-2 py-1 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="0.00"
                      />
                    </div>
                    {!viewOnly && (
                      <button
                        onClick={() => removeRarity(idx)}
                        className="px-3 py-1 text-sm text-red-600 hover:bg-red-100 rounded"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button onClick={onClose} variant="secondary">
              {viewOnly ? 'Close' : 'Cancel'}
            </Button>
            {!viewOnly && (
              <Button onClick={handleSave}>
                Save Changes
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Main Page ---------------- */

export default function SetManagement() {
  const navigate = useNavigate();
  const [sets, setSets] = useState([]);
  const [languages, setLanguages] = useState(["EN", "JP"]);
  const [games, setGames] = useState(["Pokemon", "Yu-Gi-Oh!", "Magic: The Gathering", "One Piece"]);
  const [loading, setLoading] = useState(true);
  const [editingSet, setEditingSet] = useState(null);

  useEffect(() => {
    loadSets();
    loadLanguages();
    loadGames();
  }, []);

  const loadLanguages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sets/languages`);
      if (response.ok) {
        const langs = await response.json();
        setLanguages(langs);
      }
    } catch (error) {
      console.error('Error loading languages:', error);
    }
  };

  const loadGames = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sets/games`);
      if (response.ok) {
        const gms = await response.json();
        setGames(gms);
      }
    } catch (error) {
      console.error('Error loading games:', error);
    }
  };

  const loadSets = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sets`);
      const data = await response.json();
      setSets(data);
    } catch (error) {
      console.error('Error loading sets:', error);
    }
    setLoading(false);
  };

  const handleUpdateSet = async (setId, updates) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sets/${setId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        alert(`Failed to update set: ${errorData.error || 'Unknown error'}`);
        return;
      }

      await loadSets();
      alert('Set updated successfully!');
    } catch (error) {
      console.error('Error updating set:', error);
      alert('Failed to update set');
    }
  };

  const handleDeleteSet = async (setId, setName) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${setName}"? This will permanently delete the set and ALL sessions associated with it. This action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sets/${setId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        alert(`Failed to delete set: ${errorData.error || 'Unknown error'}`);
        return;
      }

      await loadSets();
      alert('Set and all associated sessions deleted successfully!');
    } catch (error) {
      console.error('Error deleting set:', error);
      alert('Failed to delete set');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading sets...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-10 space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Manage Sets</h1>
            <p className="text-gray-500 mt-1">View and edit Pokémon card sets</p>
          </div>
          <div className="flex gap-2">
            {process.env.REACT_APP_SET_CREATION === 'enabled' && (
              <Button onClick={() => navigate('/sets/new')} variant="secondary">
                Create New Set
              </Button>
            )}
            <Button onClick={() => navigate('/dashboard')} variant="secondary">
              Back to Dashboard
            </Button>
          </div>
        </div>

        {/* Sets List */}
        <Card>
          {sets.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">No sets found</p>
              {process.env.REACT_APP_SET_CREATION === 'enabled' && (
                <Button 
                  onClick={() => navigate('/sets/new')} 
                  className="mt-4"
                >
                  Create Your First Set
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-gray-900">Name</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-900">Code</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-900">Game</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-900">Language</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-900">Release Date</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-900">Rarities</th>
                    <th className="text-right p-4 text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sets.map((set) => (
                    <tr key={set._id} className="border-b hover:bg-gray-50">
                      <td className="p-4 text-sm text-gray-900 font-medium">
                        {set.name}
                      </td>
                      <td className="p-4 text-sm text-gray-700">
                        {set.code}
                      </td>
                      <td className="p-4 text-sm text-gray-700">
                        {set.game || 'Pokemon'}
                      </td>
                      <td className="p-4 text-sm text-gray-700">
                        {set.language}
                      </td>
                      <td className="p-4 text-sm text-gray-700">
                        {set.release ? new Date(set.release).toLocaleDateString() : '-'}
                      </td>
                      <td className="p-4 text-sm text-gray-700">
                        <div className="flex flex-wrap gap-1">
                          {set.rarities.map((rarity, idx) => (
                            <span 
                              key={idx}
                              className="inline-block px-2 py-1 bg-gray-100 rounded text-xs"
                            >
                              {rarity.name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            onClick={() => setEditingSet(set)}
                            variant="secondary"
                            className="text-xs"
                          >
                            {process.env.REACT_APP_SET_CREATION === 'enabled' ? 'Edit' : 'View'}
                          </Button>
                          {process.env.REACT_APP_SET_CREATION === 'enabled' && (
                            <Button
                              onClick={() => handleDeleteSet(set._id, set.name)}
                              variant="danger"
                              className="text-xs"
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Edit Modal */}
        {editingSet && (
          <EditSetModal
            set={editingSet}
            languages={languages}
            games={games}
            viewOnly={process.env.REACT_APP_SET_CREATION !== 'enabled'}
            onClose={() => setEditingSet(null)}
            onSave={handleUpdateSet}
          />
        )}
      </div>
    </div>
  );
}
